import type { Instance } from "mobx-state-tree";
import { types, flow } from "mobx-state-tree";
import * as repo from "./repository";
import { Role } from "./models";
import type { PermissionRow, RoleSnapshotIn } from "./models";
import type { TablesInsert, TablesUpdate } from "@/types/supabase-helpers";

export const RolesStore = types
  .model("RolesStore", {
    roles: types.array(Role),
    permissions: types.frozen<PermissionRow[]>([]),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .views((self) => ({
    get rolesList() {
      return self.roles.slice();
    },
    get permissionsList() {
      return self.permissions;
    },
    getByCode(code: string) {
      return self.roles.find((r) => r.code === code);
    },
  }))
  .actions((self) => ({
    loadAll: flow(function* loadAll() {
      self.isLoading = true;
      self.error = null;
      try {
        const [rolesData, permissionsData]: [
          repo.RoleWithPermissions[],
          PermissionRow[],
        ] = yield Promise.all([repo.listRolesWithPermissions(), repo.listAllPermissions()]);
        self.roles.clear();
        rolesData.forEach((r) => {
          self.roles.push({
            code: r.code,
            name: r.name,
            description: r.description,
            scope: r.scope,
            deprecated: r.deprecated,
            created_at: r.created_at,
            permissionCodes: r.permissionCodes,
          } as RoleSnapshotIn);
        });
        self.permissions = permissionsData;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    createRole: flow(function* createRole(input: TablesInsert<"roles">) {
      self.error = null;
      try {
        const created = yield repo.createRole(input);
        self.roles.push({
          code: created.code,
          name: created.name,
          description: created.description,
          scope: created.scope,
          deprecated: created.deprecated,
          created_at: created.created_at,
          permissionCodes: [],
        } as RoleSnapshotIn);
        return created;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    updateRole: flow(function* updateRole(code: string, updates: TablesUpdate<"roles">) {
      self.error = null;
      try {
        const updated = yield repo.updateRole(code, updates);
        const existing = self.roles.find((r) => r.code === code);
        if (existing) {
          existing.name = updated.name;
          existing.description = updated.description;
          existing.scope = updated.scope;
          existing.deprecated = updated.deprecated;
        }
        return updated;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    deleteRole: flow(function* deleteRole(code: string) {
      self.error = null;
      try {
        yield repo.deleteRole(code);
        const idx = self.roles.findIndex((r) => r.code === code);
        if (idx !== -1) self.roles.splice(idx, 1);
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    setPermissions: flow(function* setPermissions(roleCode: string, permissionCodes: string[]) {
      self.error = null;
      try {
        yield repo.setRolePermissions(roleCode, permissionCodes);
        const role = self.roles.find((r) => r.code === roleCode);
        if (role) {
          role.permissionCodes.replace(permissionCodes);
        }
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    clear() {
      self.roles.clear();
      self.permissions = [];
      self.isLoading = false;
      self.error = null;
    },
  }));

export type RolesStoreInstance = Instance<typeof RolesStore>;
